import { useCallback, useEffect, useState } from "react";
import { Share2, LogOut, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { Home } from "./Home";
import { Viewer } from "./Viewer";
import { Preferences } from "./Preferences";
import { AccountArea } from "../account/AccountArea";
import { loadViewerData, putPreferences, scopeLabel } from "./api5";
import "./ww.css";

const HERO_IMG = "/brand/www-hero.jpg";

const DEFAULT_PREFS = { country_codes: [], continent_codes: [], date_from_year: null, date_to_year: null, slideshow_seconds: 10, always_show_details: true };

function useIsMobile() {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h); return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

export function WorldExperience({ me, onLogout, onShare, onAccountChanged, privileged, onAdmin }) {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [view, setView] = useState("home");
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const isMobile = useIsMobile();

  // One authoritative preference/geography load per account (see api5.loadViewerData); a
  // remount or a refreshed `me` object for the same account reuses it instead of re-fetching.
  const uid = me?.www_user_id;
  useEffect(() => {
    let live = true;
    (async () => {
      const v = await loadViewerData(uid);
      if (!live) return;
      if (v.prefs) setPrefs({ ...DEFAULT_PREFS, ...v.prefs });
      setCountries(v.countries);
      setContinents(v.continents);
      if (v.status === 403) toast.message("Subscriber access unlocks the whole world — ask an admin to enable it on your account.");
    })();
    return () => { live = false; };
  }, [uid]);

  // Deep-link from the phone's post-pairing "Change filters" action (/?filters=1):
  // open the existing Preferences ("Change where & when") modal, then scrub the param.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("filters") === "1") {
      setPrefsOpen(true);
      try { window.history.replaceState({}, document.title, window.location.pathname); } catch (_e) { /* noop */ }
    }
  }, []);

  const scope = scopeLabel(prefs, countries, continents);

  const applyPrefs = useCallback(async (body) => {
    const r = await putPreferences(body);
    if (!r.ok) { toast.error(r.data?.detail || "Could not save your preferences"); return; }
    setPrefs({ ...DEFAULT_PREFS, ...r.data });
    setPrefsOpen(false);
    setReloadKey((k) => k + 1);
    setView("viewer");
  }, []);

  const wholeWorld = useCallback(async () => {
    await applyPrefs(DEFAULT_PREFS);
  }, [applyPrefs]);

  // Reset filters (item 2): clear location/time selections AND the persisted
  // preferences, then immediately refresh the stream to the default/unfiltered
  // (whole world) state. Slideshow speed is a viewing preference, not a filter,
  // so it is preserved.
  const resetFilters = useCallback(async () => {
    await applyPrefs({ ...DEFAULT_PREFS, slideshow_seconds: prefs.slideshow_seconds || 10, always_show_details: prefs.always_show_details });
    toast.success("Filters cleared — showing the whole world.");
  }, [applyPrefs, prefs.slideshow_seconds, prefs.always_show_details]);

  // Item 5 — "Clear all filters" from the landing page: clear all location/time
  // preferences + persisted state and return to "The whole world · Any time",
  // WITHOUT forcing entry into the Viewer (stays on the landing page).
  const filtersActive = (prefs.country_codes?.length || prefs.continent_codes?.length
    || prefs.date_from_year != null || prefs.date_to_year != null);
  const clearAllFilters = useCallback(async () => {
    const body = { ...DEFAULT_PREFS, slideshow_seconds: prefs.slideshow_seconds || 10, always_show_details: prefs.always_show_details };
    const r = await putPreferences(body);
    if (!r.ok) { toast.error(r.data?.detail || "Could not clear your filters"); return; }
    setPrefs({ ...DEFAULT_PREFS, ...r.data });
    setReloadKey((k) => k + 1);
    toast.success("Filters cleared — showing the whole world.");
  }, [prefs.slideshow_seconds, prefs.always_show_details]);

  const persistSpeed = useCallback((s) => {
    setPrefs((prev) => { const body = { ...prev, slideshow_seconds: s }; putPreferences(body); return body; });
  }, []);

  return (
    <div className={`ww-app ${view === "home" ? "ww-app-home" : ""}`} data-testid="world-experience">
      {view === "home" && (
        <>
          <header className="ww-header">
            <div className="ww-brand" data-testid="ww-brand">
              <img src="/brand/www-logo-horizontal.png" className="ww-brand-logo ww-brand-logo-full"
                   alt="World Wide Wallpaper — A world of joy, freely shared." />
              <img src="/brand/www-emblem.png" className="ww-brand-logo ww-brand-logo-emblem"
                   alt="World Wide Wallpaper" loading="lazy" />
            </div>
            <nav className="ww-nav">
              <span className="ww-nav-links" style={{ display: "flex", gap: 28 }}>
                <button data-testid="nav-show-world" onClick={() => setView("viewer")}>Show Me The World</button>
                <button data-testid="nav-share" onClick={onShare}>Share</button>
                {privileged && (
                  <button data-testid="nav-admin" className="ww-nav-admin" onClick={onAdmin}>
                    <Shield size={15} strokeWidth={1.8} /> Moderation
                  </button>
                )}
              </span>
              <div className="ww-account">
                <button className="ww-avatar ww-avatar-btn" title="Your account" aria-label="Open your account"
                        data-testid="nav-account" onClick={() => setAccountOpen(true)}>
                  {(me?.email || "?")[0].toUpperCase()}
                </button>
                <button data-testid="nav-logout" onClick={onLogout} aria-label="Log out"><LogOut size={16} /></button>
              </div>
            </nav>
          </header>

          <main className="ww-main">
            <Home
              scope={scope}
              heroImg={HERO_IMG}
              filtersActive={filtersActive}
              onShowWorld={() => { setReloadKey((k) => k + 1); setView("viewer"); }}
              onChangeWhereWhen={() => setPrefsOpen(true)}
              onClearFilters={clearAllFilters}
              onShare={onShare}
            />
          </main>
        </>
      )}

      {view === "viewer" && (
        <main className="ww-main">
          <Viewer
            prefs={prefs}
            countries={countries}
            continents={continents}
            reloadKey={reloadKey}
            onExit={() => setView("home")}
            onOpenPrefs={() => setPrefsOpen(true)}
            onWholeWorld={wholeWorld}
            onPersistSpeed={persistSpeed}
          />
        </main>
      )}

      <Preferences
        open={prefsOpen}
        isMobile={isMobile}
        onClose={() => setPrefsOpen(false)}
        prefs={prefs}
        countries={countries}
        continents={continents}
        onApply={applyPrefs}
        onReset={resetFilters}
      />

      {accountOpen && (
        <div className="ww-modal-overlay" onClick={() => setAccountOpen(false)}>
          <div className="ww-panel ww-modal ww-account-modal" style={{ position: "relative" }}
               onClick={(e) => e.stopPropagation()} data-testid="account-modal">
            <button className="ww-modal-close" onClick={() => setAccountOpen(false)}
                    data-testid="account-modal-close" aria-label="Close your account"><X size={22} /></button>
            <AccountArea me={me} onLogout={onLogout} onChanged={onAccountChanged} />
          </div>
        </div>
      )}
    </div>
  );
}
